const BASE_URL = 'https://api.dictionaryapi.dev/api/v2/entries/en';
const RECENT_KEY = 'recentWords';
const RECENT_LIMIT = 8;

// Rotates by day of the month, so every visitor sees the same word on a given date
const WORD_OF_THE_DAY_LIST = [
  'serendipity', 'ephemeral', 'ubiquitous', 'resilience', 'sadistic',
  'mellifluous', 'petrichor', 'luminous', 'eloquent', 'vivacious'
];

const SUGGESTION_WORDS = [
  'serendipity', 'ephemeral', 'ubiquitous', 'resilience', 'sadistic',
  'beautiful', 'incredible', 'magnificent', 'wonderful', 'fantastic',
  'amazing', 'brilliant', 'excellent', 'perfect', 'outstanding',
  'remarkable', 'extraordinary', 'exceptional', 'phenomenal', 'spectacular',
  'marvelous', 'superb', 'fabulous', 'tremendous', 'awesome',
  'unbelievable', 'astonishing', 'breathtaking', 'stunning', 'petrichor'
];

const WORD_LISTS = [
  { name: 'Everyday words', words: ['beautiful', 'important', 'different', 'possible'] },
  { name: 'Academic words', words: ['analyze', 'synthesize', 'evaluate', 'demonstrate'] },
  { name: 'Advanced vocabulary', words: ['perspicacious', 'magnanimous', 'quintessential', 'paradigm'] },
  { name: 'Business terms', words: ['synergy', 'leverage', 'optimize', 'stakeholder'] }
];

const REGION_NAMES = { us: 'US', uk: 'UK', au: 'Australian', ca: 'Canadian' };
const REGION_ORDER = ['US', 'UK', 'Australian', 'Canadian', ''];

// The API returns one entry per origin (bank as in river, bank as in money).
// Fold them into one entry grouped by part of speech, the way the page shows it.
export const mergeEntries = (entries) => {
  const first = entries[0];
  const groups = new Map();
  const audio = [];
  const phoneticTexts = [];

  entries.forEach((entry) => {
    (entry.phonetics || []).forEach((p) => {
      if (p.text && !phoneticTexts.includes(p.text)) phoneticTexts.push(p.text);
      if (!p.audio) return;
      const code = p.audio.match(/-(us|uk|au|ca)\.mp3$/i)?.[1]?.toLowerCase();
      const region = code ? REGION_NAMES[code] : '';
      if (audio.some((clip) => clip.region === region)) return;
      audio.push({ region, url: p.audio, text: p.text || '' });
    });

    (entry.meanings || []).forEach((meaning) => {
      const pos = meaning.partOfSpeech;
      if (!groups.has(pos)) {
        groups.set(pos, { partOfSpeech: pos, senses: [], synonyms: new Set(), antonyms: new Set() });
      }
      const group = groups.get(pos);
      (meaning.definitions || []).forEach((d) => {
        if (group.senses.some((s) => s.definition === d.definition)) return;
        group.senses.push({
          definition: d.definition,
          example: d.example || '',
          synonyms: d.synonyms || [],
          antonyms: d.antonyms || []
        });
      });
      (meaning.synonyms || []).forEach((w) => group.synonyms.add(w));
      (meaning.antonyms || []).forEach((w) => group.antonyms.add(w));
    });
  });

  return {
    word: first.word,
    phonetic: first.phonetic || phoneticTexts[0] || '',
    audio: audio.sort((a, b) => REGION_ORDER.indexOf(a.region) - REGION_ORDER.indexOf(b.region)),
    groups: [...groups.values()].map((g) => ({
      ...g,
      synonyms: [...g.synonyms],
      antonyms: [...g.antonyms]
    })),
    origin: entries.find((e) => e.origin)?.origin || '',
    sourceUrls: first.sourceUrls || [],
    license: first.license || null
  };
};

export class DictionaryService {
  static async getEntry(word) {
    const response = await fetch(`${BASE_URL}/${encodeURIComponent(word.toLowerCase())}`);
    if (!response.ok) {
      throw new Error(response.status === 404 ? 'not-found' : 'unavailable');
    }
    const data = await response.json();
    return mergeEntries(data);
  }

  static async searchSuggestions(query) {
    // The free API has no suggestion endpoint, so suggestions come from a fixed list
    if (!query || query.length < 2) return [];

    return SUGGESTION_WORDS
      .filter(word => word.startsWith(query.toLowerCase()))
      .slice(0, 5);
  }

  static getPopularWords() {
    return ['serendipity', 'ephemeral', 'ubiquitous', 'resilience', 'sadistic'];
  }

  static getWordLists() {
    return WORD_LISTS;
  }

  static getWordForDate(date) {
    return WORD_OF_THE_DAY_LIST[date.getDate() % WORD_OF_THE_DAY_LIST.length];
  }

  // Resolves with entry: null when the definition can't be fetched,
  // so the page can still show the word and link to it
  static async getWordOfTheDay(date = new Date()) {
    const word = this.getWordForDate(date);
    try {
      return { word, entry: await this.getEntry(word) };
    } catch {
      return { word, entry: null };
    }
  }

  static getRecentWords() {
    try {
      const saved = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(saved) ? saved : [];
    } catch {
      return [];
    }
  }

  static addRecentWord(word) {
    try {
      const recent = this.getRecentWords().filter((w) => w !== word);
      localStorage.setItem(RECENT_KEY, JSON.stringify([word, ...recent].slice(0, RECENT_LIMIT)));
    } catch {
      // Storage blocked: recent words just aren't remembered
    }
  }
}
